import { useState, useEffect, useCallback } from 'react';
import contactService from '../services/contactService';
import { processContactsData } from '../utils/contactUtils';
import { UX_TIMING, OPERATION_STATUS } from '../constants';

/**
 * Hook personalizado para gestión completa de contactos
 * @returns {Object} - Estado y funciones para gestionar contactos
 */
export const useContacts = () => {
  // Estados principales
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados de paginación - NUEVO
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
    hasNext: false,
    hasPrev: false
  });

  // Estados de operaciones
  const [operationStatus, setOperationStatus] = useState(null);
  const [operatingContactName, setOperatingContactName] = useState('');
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [operationError, setOperationError] = useState(null);

  /**
   * Carga contactos y estadísticas desde el backend con paginación
   */
  const loadContacts = useCallback(async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      setError(null);
      
      const [contactsResponse, statsResponse] = await Promise.all([
        contactService.getAllContacts({ page, limit }),
        contactService.getContactStats()
      ]);
      
      if (contactsResponse.success) {
        const contactsData = contactsResponse.data?.contacts || contactsResponse.data || [];
        const processedContacts = processContactsData(contactsData);
        setContacts(processedContacts);
        
        if (contactsResponse.data?.pagination) {
          setPagination(contactsResponse.data.pagination);
        }
      }
      
      if (statsResponse.success) {
        const statsData = statsResponse.data?.stats || statsResponse.data || { total: 0, active: 0, inactive: 0 };
        setStats(statsData);
      }
      
    } catch (err) {
      setError(err.message || 'Error cargando los datos de contactos');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cambiar a una página específica
   */
  const goToPage = useCallback(async (page) => {
    await loadContacts(page, pagination.limit);
  }, [loadContacts, pagination.limit]);

  /**
   * Ir a la página siguiente
   */
  const nextPage = useCallback(async () => {
    if (pagination.hasNext) {
      await goToPage(pagination.page + 1);
    }
  }, [pagination.hasNext, pagination.page, goToPage]);

  /**
   * Ir a la página anterior
   */
  const prevPage = useCallback(async () => {
    if (pagination.hasPrev) {
      await goToPage(pagination.page - 1);
    }
  }, [pagination.hasPrev, pagination.page, goToPage]);

  /**
   * Cambiar el número de elementos por página
   */
  const changePageSize = useCallback(async (newLimit) => {
    await loadContacts(1, newLimit); // Reiniciar a página 1 con nuevo tamaño
  }, [loadContacts]);

  /**
   * Ejecuta una operación de contacto con UX optimizada
   */
  const executeContactOperation = useCallback(async (operation, contactData, isEdit = false) => {
    setOperatingContactName(contactData.nombre || 'Contacto');
    setOperationStatus(OPERATION_STATUS.LOADING);
    setIsEditingMode(isEdit);

    const startTime = Date.now();

    try {
      const response = await operation();

      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, UX_TIMING.LOADING_MIN_TIME - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      if (response.success) {
        setOperationStatus(OPERATION_STATUS.SUCCESS);
        await loadContacts();
        return { success: true };
      } else {
        setOperationStatus(OPERATION_STATUS.ERROR);
        setOperationError(response.message || 'Ocurrió un error inesperado.');
        return { success: false, message: response.message };
      }

    } catch (error) {
      console.error('Error en operación de contacto:', error);
      setOperationStatus(OPERATION_STATUS.ERROR);
      setOperationError(error.message || 'No se pudo conectar con el servidor.');
      return { success: false, message: error.message };
    }
  }, [loadContacts]);

  /**
   * Crea un nuevo contacto
   */
  const createContact = useCallback(async (contactData) => {
    return executeContactOperation(
      () => contactService.createContact(contactData),
      contactData,
      false
    );
  }, [executeContactOperation]);

  /**
   * Actualiza un contacto existente
   */
  const updateContact = useCallback(async (contactId, contactData) => {
    return executeContactOperation(
      () => contactService.updateContact(contactId, contactData),
      contactData,
      true
    );
  }, [executeContactOperation]);

  /**
   * Elimina un contacto
   */
  const deleteContact = useCallback(async (contactId, contactData = { nombre: 'Contacto' }) => {
    return executeContactOperation(
      () => contactService.deleteContact(contactId),
      contactData,
      false
    );
  }, [executeContactOperation]);

  /**
   * Resetea el estado de operación
   */
  const resetOperationState = useCallback(() => {
    setOperationStatus(OPERATION_STATUS.IDLE);
    setOperatingContactName('');
    setIsEditingMode(false);
    setOperationError(null);
  }, []);

  // Cargar datos al montar el componente
  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  return {
    // Estados
    contacts,
    stats,
    loading,
    error,
    operationStatus,
    operatingContactName,
    isEditingMode,
    operationError,
    pagination, // Exponer el estado de paginación
    
    // Funciones
    loadContacts,
    createContact,
    updateContact,
    deleteContact,
    resetOperationState,
    goToPage, // Exponer funciones de paginación
    nextPage,
    prevPage,
    changePageSize,
  };
}; 